import errorCallbackFactory from "util/errorCallbackFactory";

/**
 * ServiceResponseValidator is a utility class that can be constructed and bound
 * to an observable to handle async validation and generate contextually helpful
 * validation messages from an http response.
 *
 * The general pattern is:
 *    1) Construct an instance of the class and extend an observable with that:
 *          var myServiceResponseValidator = new ServiceResponseValidator(myErrorHandlers);
 *          myObservable.extend({responseValidator: myServiceResponseValidator});
 *    2) In the then/catch handlers for your service request, either handleErrorInfo,
 *       createErrorCallback, or handleSuccess with the http response
 *
 *  See the unit tests for examples.
 *
 * # Required Arguments
 *
 * @param {Object} errorCodeHandlers - A map of functions where the key is the error code
 *                 and the value is a function that returns an object with a validation message.
 *
 * # Options:
 *    @param {Function} getErrorFromHttpResponse: Specify if you need a custom function to pull the error information
 *                      from the http response. The default should work for errors from the public-api or from back-end
 *                      services that use public-api's ErrorInfo contract.
 */
export default function (errorCodeHandlers, options) {
    const self = this;
    let validationCallbacks;

    if (!errorCodeHandlers) {
        throw "ServiceResponseValidator requires errorCodeHandlers to be defined.";
    }

    // This is meant to strip out the error info from httpResponse in a format using the typical
    // errorInfo format from public-api. There are two ways I've seen it: one for errors generated
    // in the public-api, and another originating in back-end services.
    // Also made it an option so that a custom one could be passed in, if necessary
    const getErrorFromHttpResponse =
        (options && options.getErrorFromHttpResponse) ||
        function (response) {
            let errorInfo;
            if (response) {
                if (response.code && response.messageParams) {
                    // This looks like a back-end service error
                    errorInfo = response;
                } else if (
                    response &&
                    response.details &&
                    response.details.response &&
                    response.details.response.code &&
                    response.details.response.messageParams
                ) {
                    // This looks like a public-api error
                    errorInfo = response.details.response;
                }
            }
            return errorInfo;
        };

    function getErrorCodeHandler(response) {
        const error = getErrorFromHttpResponse(response);
        if (error) {
            const code = error.code;
            if (errorCodeHandlers[code]) {
                return function (value) {
                    return errorCodeHandlers[code](error, value);
                };
            }
        }
    }

    // This object is used as the params in the validator, so to get the correct params into renderString
    // it's necessary to do that work in this validator. Set this flag to avoid having them attempt to
    // do the localization lookup again later.
    self.isMessageLocalized = true;

    // This is used by the ko validation extension. Typical clients of this class can ignore it.
    self.setValidationCallbacks = function (resolve, reject) {
        validationCallbacks = {
            resolve: resolve,
            reject: reject,
        };

        return validationCallbacks;
    };

    self.handleErrorInfo = function (error, options) {
        const errorHandler = getErrorCodeHandler(error);
        if (errorHandler && validationCallbacks) {
            validationCallbacks.reject(errorHandler);
            validationCallbacks = null;
        } else if (options && options.callbackConfig) {
            return errorCallbackFactory.createCallback(options.callbackConfig)(
                error
            );
        }
    };

    self.createErrorCallback = function (callbackConfig) {
        if (!callbackConfig) {
            throw "ServiceResponseValidator must be initialized with an errorCallbackFactory config to use for graceful fallback";
        }

        return function (error) {
            return self.handleErrorInfo(error, {
                callbackConfig: callbackConfig,
            });
        };
    };

    self.handleSuccess = function () {
        if (validationCallbacks) {
            validationCallbacks.resolve();
            validationCallbacks = null;
        }
    };
}
